// src/pages/DashboardPage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Imports do Material-UI
import { Box, Paper, Typography, Button, Container, Grid, styled, alpha } from '@mui/material';
import { keyframes } from '@mui/system';
import { Insights, TrackChanges, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';

// ============================================================================
// COMPONENTES ESTILIZADOS (IDÊNTICOS AO SEU EXEMPLO)
// ============================================================================

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const FeatureCard = styled(Paper)(({ theme, delay = 0, glowColor }) => ({
    display: 'flex',          // Adicionado para controle de altura
    flexDirection: 'column', // Adicionado para controle de altura
    position: 'relative',
    overflow: 'hidden',
    padding: theme.spacing(4),
    height: '100%',
    borderRadius: '20px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.07)',
    transition: 'transform 0.3s ease, box-shadow 0.4s ease',
    // Animação de entrada restaurada
    animation: `${fadeInUp} 0.6s ease-out forwards`,
    animationDelay: `${delay}s`,
    opacity: 0,
    border: '1px solid #E5E7EB',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `radial-gradient(circle at 50% 0, ${alpha(glowColor, 0.15)}, transparent 40%)`,
        opacity: 0,
        transition: 'opacity 0.4s ease',
    },
    '&:hover': {
        transform: 'translateY(-12px)',
        boxShadow: `0 20px 40px ${alpha(glowColor, 0.2)}`,
        '&:before': {
            opacity: 1,
        }
    },
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
    width: 64,
    height: 64,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2.5),
    color: '#FFFFFF',
    background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
    boxShadow: `0 6px 12px ${alpha(color, 0.3)}`,
    fontSize: '32px',
}));

// ============================================================================
// COMPONENTE DA PÁGINA DASHBOARD
// ============================================================================

const DashboardPage = () => {
    const auth = useAuth();
    const navigate = useNavigate();

    const modules = [
        {
            icon: <Insights sx={{ fontSize: 'inherit' }} />,
            color: '#3B82F6',
            glowColor: '#3B82F6', // Cor para o efeito de brilho
            title: 'Definir Business Awareness',
            description: 'Estabeleça a missão, visão, valores e outros elementos fundamentais da sua estratégia.',
            delay: 0.2,
            path: '/business-awareness',
            disabled: false
        },
       {
            icon: <TrackChanges sx={{ fontSize: 'inherit' }} />,
            color: '#8B5CF6',
            glowColor: '#8B5CF6', // Cor para o efeito de brilho
            title: 'Definir Objetivos Estratégicos',
            description: 'Crie e acompanhe os objetivos que guiarão sua empresa ao sucesso.',
            delay: 0.3,
            path: '/strategic-objectives',
            disabled: false
        },
    ];

    return (
        <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                <Typography
                    variant="h3" component="h1"
                    sx={{
                        fontWeight: 800,
                        color: '#111827',
                        animation: `${fadeInUp} 0.5s ease-out`,
                        mb: 1
                    }}
                >
                    Elementos da Estratégia
                </Typography>
                <Typography
                    variant="h6" component="p"
                    sx={{
                        color: '#6B7280',
                        fontWeight: 400,
                        maxWidth: '750px',
                        mx: 'auto',
                        animation: `${fadeInUp} 0.5s ease-out 0.1s`,
                        opacity: 0,
                        animationFillMode: 'forwards'
                    }}
                >
                    Bem-vindo(a), <Box component="span" sx={{ color: 'primary.main', fontWeight: '600' }}>{auth.user?.name || 'Usuário'}</Box>! Selecione uma opção para começar.
                </Typography>
            </Box>

            <Grid container spacing={4} justifyContent="center">
                {modules.map((mod) => (
                    <Grid item xs={12} sm={6} md={5} lg={3} key={mod.title} sx={{ display: 'flex' }}>
                        <FeatureCard
                            elevation={0}
                            delay={mod.delay}
                            glowColor={mod.glowColor}
                            onClick={() => !mod.disabled && navigate(mod.path)}
                            sx={{ cursor: mod.disabled ? 'not-allowed' : 'pointer', opacity: mod.disabled ? 0.6 : 1 }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flexGrow: 1 }}>
                                <IconWrapper color={mod.color}>{mod.icon}</IconWrapper>
                                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#1F2937', mb: 1.5 }}>{mod.title}</Typography>
                                <Typography variant="body2" sx={{ color: '#6B7280', flexGrow: 1, minHeight: '60px' }}>{mod.description}</Typography>
                                
                                {/* Botão com estilo e comportamento atualizados */}
                                <Button
                                    variant="text"
                                    size="small"
                                    endIcon={<ArrowForwardIcon />}
                                    disabled={mod.disabled}
                                    sx={{ 
                                        mt: 3, 
                                        fontWeight: 600, 
                                        color: mod.color, 
                                        '&:hover': { 
                                            backgroundColor: alpha(mod.color, 0.08) 
                                        } 
                                    }}
                                >
                                    {mod.disabled ? 'Em Breve' : 'Acessar Módulo'}
                                </Button>
                            </Box>
                        </FeatureCard>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default DashboardPage;